import { FC, useCallback, useMemo, useRef, useState } from 'react'

import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  ClientManager,
  ClientManagerRef,
} from '@/containers/billing/register/billingPortals/clientManager'
import { BillingFormData } from '@/containers/logistic/purchaseOrders/wizard/NewPurchaseOrderWizard'
import { useAreasQuery } from '@/hooks/business/useAreasQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessUserQuery } from '@/hooks/business/useBusinessUserQuery'

interface ClientInfoStepProps {
  formData: BillingFormData
  onNext: (data: Partial<BillingFormData>) => void
  setIsMainButtonDisabled: (disabled: boolean) => void
}

const ClientInfoStep: FC<ClientInfoStepProps> = ({
  formData,
  onNext,
  setIsMainButtonDisabled,
}) => {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})

  // Local state for client data and validation
  const [isValidating, setIsValidating] = useState(false)
  const [isClientDataValid, setIsClientDataValid] = useState(true) // Start as valid to allow interaction
  const [clientManagerData, setClientManagerData] =
    useState<Partial<BillingFormData>>(formData)
  const clientNotesRef = useRef<HTMLTextAreaElement>(null)

  // Ref to access ClientManager submit function
  const clientManagerRef = useRef<ClientManagerRef>(null)

  const priceSystems = useMemo(() => {
    if (!businessData?.priceSystems || businessData.priceSystems.length === 0) {
      return []
    }
    return businessData.priceSystems
  }, [businessData])

  const { user, allowRoles, isLoadingUser } = useBusinessUserQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  const { areas: areasRawData, isLoadingAreas } = useAreasQuery({
    params: {
      businessId: businessData?.id ?? 0,
    },
    options: {
      enabled:
        !!businessData?.id &&
        !isLoadingBusinessData &&
        !isErrorBusinessData &&
        !isLoadingUser,
    },
  })

  const areas = useMemo(() => {
    if (allowRoles(['OWNER'], true)) {
      return areasRawData
    }

    return areasRawData?.filter(
      (area) =>
        area.isActive &&
        user?.allowedSalesAreas?.some((item) => item.id === area.id)
    )
  }, [areasRawData, user])

  // Calculate if main button should be disabled
  const isMainButtonDisabledState = useMemo(() => {
    return (
      isLoadingBusinessData ||
      isLoadingUser ||
      isLoadingAreas ||
      isValidating ||
      !isClientDataValid
    )
  }, [
    isLoadingBusinessData,
    isLoadingUser,
    isLoadingAreas,
    isValidating,
    isClientDataValid,
  ])

  // Update parent's main button state whenever our state changes
  useMemo(() => {
    setIsMainButtonDisabled(isMainButtonDisabledState)
  }, [isMainButtonDisabledState, setIsMainButtonDisabled])

  // Handle validation changes from ClientManager
  const handleValidationChange = useCallback((isValid: boolean) => {
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('ClientInfoStep - Validation changed:', isValid)
    }
    setIsClientDataValid(isValid)
  }, [])

  // Handle data changes from ClientManager (for synchronization only)
  const handleDataChange = useCallback((data: any) => {
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('ClientInfoStep - Data sync update:', data)
    }
    // Just update local state for synchronization, don't advance wizard
    setClientManagerData(data)
  }, [])

  // Handle successful submission from ClientManager (only called on successful submit)
  const handleClientSubmit = useCallback(
    (data: any) => {
      if (process.env.REACT_APP_ENV === 'development') {
        console.log('ClientInfoStep - Successful submission received:', data)
      }
      setIsValidating(true)

      try {
        // Process the client data and create the BillingFormData
        let billingData: Partial<BillingFormData> = {
          clientNotes: clientNotesRef.current?.value || '',
          isNewClient: data.isNewClient,
        }

        if (data.isNewClient) {
          // Creating new client
          billingData.newClientData = data.newClientData
          billingData.clientId = undefined
          billingData.selectedClientData = undefined
          billingData.commercialId = undefined // New clients don't have assigned commercials
          billingData.isNewCommercial = true

          // Map address data from new client to newAddressData for DeliveryDetailsStep
          if (data.newClientData) {
            billingData.newAddressData = {
              mainAddress: data.newClientData.mainAddress,
              secondaryAddress: data.newClientData.secondaryAddress,
              locality: data.newClientData.locality,
              country: data.newClientData.country,
              province: data.newClientData.province,
              municipality: data.newClientData.municipality,
            }
            // For new clients, we should auto-show the address form with prefilled data
            billingData.isNewAddress = true
          }

          if (process.env.REACT_APP_ENV === 'development') {
            console.log(
              'ClientInfoStep - Passing data for new client:',
              billingData
            )
          }
        } else {
          // Selected existing client
          billingData.clientId = data.clientId
          billingData.selectedClientData = data.selectedClientData
          billingData.newClientData = undefined
          // Clear address data when switching to existing client
          billingData.newAddressData = undefined
          billingData.isNewAddress = false

          // Set commercialId if the client has an assigned commercial
          if (data.selectedClientData?.commercial?.id) {
            billingData.commercialId =
              data.selectedClientData.commercial.id.toString()
            billingData.isNewCommercial = false
          } else {
            billingData.commercialId = undefined
            billingData.isNewCommercial = true
          }

          if (process.env.REACT_APP_ENV === 'development') {
            console.log(
              'ClientInfoStep - Passing data for existing client:',
              billingData
            )
          }
        }

        // Set default price system and area
        billingData.selectedPriceSystemId =
          (priceSystems?.find((priceSystem) => priceSystem.isMain)
            ? priceSystems?.find((priceSystem) => priceSystem.isMain)
            : priceSystems?.[0]
          )?.id ?? -1
        billingData.selectedAreaSalesId = areas?.[0]?.id ?? -1

        // NOW advance the wizard
        if (process.env.REACT_APP_ENV === 'development') {
          console.log('ClientInfoStep - Advancing wizard to next step')
        }
        onNext(billingData)
      } catch (error) {
        console.error('Error processing client data:', error)
      } finally {
        setIsValidating(false)
      }
    },
    [priceSystems, areas, onNext]
  )

  // Direct submit handler for the hidden button (backward compatibility)
  const handleNextClick = async () => {
    if (process.env.REACT_APP_ENV === 'development') {
      console.log(
        'ClientInfoStep - Hidden button clicked, calling ClientManager submit'
      )
    }

    try {
      await clientManagerRef.current?.submit()
    } catch (error) {
      console.error('Error submitting client manager:', error)
    }
  }

  return (
    <div className="space-y-6">
      {/* Self-contained Client Management */}
      <ClientManager
        ref={clientManagerRef}
        onSubmit={handleClientSubmit}
        onDataChange={handleDataChange}
        onValidationChange={handleValidationChange}
        data={clientManagerData as any}
        disabled={
          isLoadingBusinessData ||
          isLoadingUser ||
          isLoadingAreas ||
          isValidating
        }
        required={true}
      />

      {/* Loading indicator */}
      {(isLoadingBusinessData || isLoadingUser || isLoadingAreas) && (
        <div className="text-sm text-muted-foreground">
          Cargando información del negocio...
        </div>
      )}

      {/* Validating indicator */}
      {isValidating && (
        <div className="text-sm text-blue-600">
          Procesando información del proveedor...
        </div>
      )}

      {/* Client Notes */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Notas sobre el proveedor</Label>
        <Textarea
          ref={clientNotesRef}
          defaultValue={formData.clientNotes || ''}
          placeholder="Añadir notas adicionales sobre el proveedor..."
          rows={3}
          disabled={
            isLoadingBusinessData ||
            isLoadingUser ||
            isLoadingAreas ||
            isValidating
          }
        />
      </div>

      {/* Hidden button for parent to trigger - backward compatibility */}
      <button
        type="button"
        onClick={handleNextClick}
        className="hidden"
        id="client-info-next"
        disabled={isMainButtonDisabledState}
      >
        Internal Next
      </button>
    </div>
  )
}

export default ClientInfoStep
